import React from 'react';
import Button from '../../components/Form/Button';
import { resolveFileUrl } from '../../utils/fileUrl';
import type { Job, Application } from './types';
import { getStatusBadge } from './types';

interface ApplicationDetailsModalProps {
  application: Application;
  jobs: Job[];
  onClose: () => void;
  onEdit: (application: Application) => void;
}

const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({
  application, jobs, onClose, onEdit,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
    <div className="relative w-full max-w-4xl mx-4 my-8">
      <div className="bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-semibold text-gray-900">Application Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">Application ID</label>
              <p className="mt-1 text-gray-900">{application.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <p className="mt-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(application.status)}`}>
                  {application.status}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Candidate Name</label>
              <p className="mt-1 text-gray-900 font-semibold">{application.candidateName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Job Title</label>
              <p className="mt-1 text-gray-900">{jobs.find(j => j.title === application.jobId)?.title || application.rawCandidate?.appliedPosition || application.jobId || 'Unknown'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-gray-900">{application.email || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="mt-1 text-gray-900">{application.phone}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Applied Date</label>
              <p className="mt-1 text-gray-900">{application.appliedDate}</p>
            </div>
            {application.rawCandidate && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-500">Nationality</label>
                  <p className="mt-1 text-gray-900">{application.rawCandidate.nationality || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                  <p className="mt-1 text-gray-900">{application.rawCandidate.dateOfBirth ? new Date(application.rawCandidate.dateOfBirth).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Experience</label>
                  <p className="mt-1 text-gray-900">{application.rawCandidate.experience ? `${application.rawCandidate.experience} years` : '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Source</label>
                  <p className="mt-1 text-gray-900 capitalize">{application.rawCandidate.source || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Expected Salary</label>
                  <p className="mt-1 text-gray-900">{application.rawCandidate.expectedSalary ? `$${application.rawCandidate.expectedSalary}` : '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Available From</label>
                  <p className="mt-1 text-gray-900">{application.rawCandidate.availableFrom ? new Date(application.rawCandidate.availableFrom).toLocaleDateString() : '—'}</p>
                </div>
                {application.rawCandidate.education && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Education</label>
                    <p className="mt-1 text-gray-900 whitespace-pre-wrap">{application.rawCandidate.education}</p>
                  </div>
                )}
                {application.rawCandidate.coverLetter && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Cover Letter / Address</label>
                    <p className="mt-1 text-gray-900 whitespace-pre-wrap">{application.rawCandidate.coverLetter}</p>
                  </div>
                )}
              </>
            )}
            <div>
              <label className="text-sm font-medium text-gray-500">Rating</label>
              <p className="mt-1 text-gray-900">
                {application.totalRating > 0 ? (
                  <span className="font-semibold">{application.totalRating}/10</span>
                ) : (
                  <span className="text-gray-400">Not rated</span>
                )}
              </p>
            </div>
            {application.resume && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-500">Resume</label>
                <p className="mt-1">
                  <Button
                    onClick={() => window.open(resolveFileUrl(application.resume), '_blank')}
                    variant="secondary"
                    size="sm"
                  >
                    📄 View Resume
                  </Button>
                </p>
              </div>
            )}
            {application.notes && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="mt-2 text-gray-900 whitespace-pre-wrap">{application.notes}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <Button onClick={onClose} variant="secondary">Close</Button>
          <Button onClick={() => { onClose(); onEdit(application); }} variant="primary">Edit Application</Button>
        </div>
      </div>
    </div>
  </div>
);

export default ApplicationDetailsModal;
